import { AdminAction, AdminButton } from 'common/components/AdminButton';
import Modal from 'common/components/Modal/Modal';
import SpinnerButton from 'common/components/SpinnerButton';
import { AppSyncToken } from 'common/models/token';
import { AppSyncUserInfo } from 'common/models/userInfo';
import React from 'react';
import {
  ichiSwapService,
  initialIchiSwapContext,
} from 'xstate/ichiSwapMachine';
import { XStateUtils } from 'xstate/xStateUtils';
import { RemoteTxEvent } from 'xstate/txMachine';
import {
  EventInputChangeArgs,
  EventUpdateAccountInfoArgs,
  IchiSwapEvent,
} from 'xstate/ichiSwapEvents';
import SimpleInput from 'common/components/SimpleInput/SimpleInput';
import { TokenIcon, TokenName } from 'common/components/TokenIcons/TokenIcon';
import { TokenUtils } from 'utils/tokenUtils';
import FormatNumber from 'common/components/FormatPercent/FormatNumber';
import debounce from 'lodash/debounce';
import useSelectedChain from 'hooks/useSelectedChain';
import { useAccount } from 'wagmi';
import { chains } from './WalletProvider/WalletProvider';

type Props = {
  isShowing: boolean;
  toggle: (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  refetchUserInfo: () => void;
  userInfo: AppSyncUserInfo;
  ichi: AppSyncToken;
  ichiV2: AppSyncToken;
  xichi: AppSyncToken;
};

const IchiSwapModalContainer: React.FC<Props> = (props) => {
  const { chainId } = useSelectedChain();
  const chain = chains.find((c) => c.id === chainId);
  const { address: accountAddress } = useAccount();

  const [amount, setAmount] = React.useState<string>(
    initialIchiSwapContext.amount
  );
  const [dataResponse, setDataResponse] = React.useState(
    initialIchiSwapContext.dataResponse
  );
  const [inputError, setInputError] = React.useState(
    initialIchiSwapContext.inputError
  );
  const [isSwapping, setIsSwapping] = React.useState(
    initialIchiSwapContext.isSwapping
  );
  const [isLoadingData, setIsLoadingData] = React.useState(
    initialIchiSwapContext.isLoadingData
  );
  const [isApproving, setIsApproving] = React.useState(
    initialIchiSwapContext.isApproving
  );
  const [isIchiSwappingDisabled, setIsIchiSwappingDisabled] = React.useState(
    initialIchiSwapContext.isIchiSwappingDisabled
  );

  const refreshData = () => {
    props.refetchUserInfo();
  };
  const debouncedRefreshData = React.useMemo(
    () => debounce(refreshData, 750),
    []
  );

  React.useEffect(() => {
    const subscription = ichiSwapService.subscribe((state) => {
      if (state.context.amount !== amount) {
        setAmount(state.context.amount);
      }

      if (state.context.dataResponse != dataResponse) {
        setDataResponse(state.context.dataResponse);
      }

      if (state.context.inputError != inputError) {
        setInputError(state.context.inputError);
      }

      if (state.context.isLoadingData !== isLoadingData) {
        setIsLoadingData(state.context.isLoadingData);
      }

      if (state.context.isSwapping !== isSwapping) {
        setIsSwapping(state.context.isSwapping);
      }

      if (state.context.isApproving !== isApproving) {
        setIsApproving(state.context.isApproving);
      }

      if (state.context.isIchiSwappingDisabled !== isIchiSwappingDisabled) {
        setIsIchiSwappingDisabled(state.context.isIchiSwappingDisabled);
      }
    });

    ichiSwapService.onEvent((e) => {
      if (e.type === RemoteTxEvent.REMOTE_TX_SUCCESS) {
        debouncedRefreshData();
      }
    });

    return subscription.unsubscribe;
  }, [
    ichiSwapService,
    amount,
    dataResponse,
    inputError,
    isSwapping,
    isApproving,
    isLoadingData,
    isIchiSwappingDisabled,
  ]);

  React.useEffect(() => {
    onChangeLegacyAmount('0');
  }, [chainId, accountAddress]);

  React.useEffect(() => {
    if (accountAddress && chainId) {
      const payload: EventUpdateAccountInfoArgs = {
        address: accountAddress,
        chainId: chainId,
      };
      ichiSwapService.send(
        IchiSwapEvent.UPDATE_ACCOUNT_INFO,
        XStateUtils.createEventPayload(payload)
      );
    }
  }, [chainId, accountAddress]);

  const onChangeLegacyAmount = (inputValue: string) => {
    const args: EventInputChangeArgs = {
      amount: inputValue,
    };
    ichiSwapService.send(
      IchiSwapEvent.INPUT_CHANGE,
      XStateUtils.createEventPayload(args)
    );
  };

  const getAmount = (type: 'legacy' | 'v2') => {
    switch (type) {
      case 'legacy':
        return TokenUtils.getTokenByName(props.userInfo, 'ichi').balance;
      case 'v2':
        return TokenUtils.getTokenByName(props.userInfo, 'ichi_v2').balance;
      default:
        throw new Error(`${type} not yet implemented!`);
    }
  };

  const modalHeader = (
    <div className="primary-text-color font-medium text-2xl pt-2">
      ICHI Upgrade
    </div>
  );

  const adminActions: AdminAction[] = [
    {
      text: 'Reset Approval',
      onClick: () => ichiSwapService.send(IchiSwapEvent.RESET_ALLOWANCE),
    },
    {
      text: 'Approve',
      onClick: () => ichiSwapService.send(IchiSwapEvent.APPROVE),
    },
  ];

  // TODO: Move to FSM
  const isSwapButtonDisabled =
    amount === '0' || isSwapping || isLoadingData || inputError != null;

  return (
    <Modal
      overflow={chain?.testnet}
      header={modalHeader}
      isShowing={props.isShowing}
      hide={props.toggle}
    >
      <div className="flex flex-col p-[20px_40px]">
        <div className="flex flex-col">
          <div className="inline-flex justify-between">
            <div className="inline-flex items-center">
              <TokenIcon
                name={TokenName.ICHI_LEGACY}
                height="47px"
                width="47px"
              />
              <div className="primary-text-color ml-2">Legacy ICHI</div>
            </div>
            <SimpleInput
              isNumber={true}
              step="1"
              value={amount}
              onChange={onChangeLegacyAmount}
            />
          </div>
          <div className="inline-flex justify-center items-center sub-text-color sub-text-size">
            <span>Balance:</span>
            <FormatNumber
              className="ml-1"
              value={getAmount('legacy')}
              useNumbro={true}
            />
            <a
              className="ml-2 primary-color cursor-pointer"
              onClick={() => onChangeLegacyAmount(`${getAmount('legacy')}`)}
            >
              Max
            </a>
          </div>

          <div className="inline-flex justify-between mt-3">
            <div className="inline-flex items-center">
              <TokenIcon name={TokenName.ICHI_V2} height="47px" width="47px" />
              <div className="primary-text-color ml-2">ICHI</div>
            </div>
            <SimpleInput
              isNumber={true}
              step="1"
              value={amount}
              disabled={true}
            />
          </div>
          <div className="inline-flex justify-center items-center sub-text-color sub-text-size">
            <span>Balance:</span>
            <FormatNumber
              className="ml-1"
              value={getAmount('v2')}
              useNumbro={true}
            />
          </div>
        </div>
        {inputError != null && (
          <div className="error-message mt-[20px] p-1 text-center">
            {inputError}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="footer-bg-color h-24 p-[20px] flex items-center justify-evenly rounded-b-[10px]">
        {dataResponse?.isApproved === false ? (
          <SpinnerButton
            className="primary-button-bg-color h-10 w-40 rounded-[10px]"
            isLoading={isApproving || isLoadingData}
            disabled={isApproving || isLoadingData}
            onClick={() => ichiSwapService.send(IchiSwapEvent.APPROVE)}
            data-dd-action-name="Approve ICHI Upgrade"
          >
            Approve
          </SpinnerButton>
        ) : (
          <SpinnerButton
            className="primary-button-bg-color h-10 w-40 rounded-[10px]"
            isLoading={isSwapping || isLoadingData}
            disabled={isSwapButtonDisabled}
            onClick={() => ichiSwapService.send(IchiSwapEvent.UPGRADE_ICHI)}
            data-dd-action-name="Upgrade ICHI"
          >
            Upgrade
          </SpinnerButton>
        )}
        {chain?.testnet && <AdminButton actions={adminActions} />}
      </div>
    </Modal>
  );
};

export default IchiSwapModalContainer;
